import { type CaseFileOcrStatus, OCR_STATUS } from "@/constants/caseFileOcrStatus";
import { FILE_VIEW_MODE } from "@/constants/fileViewMode";
import { useEffect, useState } from "react";

import { isAxiosHttpStatus } from "../utils/errorUtils";
import { useGetSignedUrl } from "./signedUrl";

export type { CaseFileOcrStatus };

/**
 * Fetches OCR plain text for a case file when enabled (view mode = OCR).
 */
export function useCaseFileOcrText(
  fileId: string | null | undefined,
  enabled: boolean
): CaseFileOcrStatus {
  const { getSignedUrl } = useGetSignedUrl();
  const [status, setStatus] = useState<CaseFileOcrStatus>({
    kind: OCR_STATUS.IDLE,
  });

  useEffect(() => {
    if (!fileId || !enabled) {
      setStatus({ kind: OCR_STATUS.IDLE });
      return;
    }

    let cancelled = false;
    setStatus({ kind: OCR_STATUS.LOADING });

    (async () => {
      try {
        const { signed_url: signedUrl } = await getSignedUrl(fileId, FILE_VIEW_MODE.OCR);
        const response = await fetch(signedUrl);
        if (!response.ok) {
          if (!cancelled) {
            setStatus(
              response.status === 404
                ? { kind: OCR_STATUS.NOT_FOUND }
                : {
                    kind: OCR_STATUS.ERROR,
                    message: "Failed to load OCR text",
                  }
            );
          }
          return;
        }
        const text = await response.text();
        if (!cancelled) setStatus({ kind: OCR_STATUS.SUCCESS, text });
      } catch (error) {
        if (cancelled) return;
        if (isAxiosHttpStatus(error, 404)) {
          setStatus({ kind: OCR_STATUS.NOT_FOUND });
          return;
        }
        setStatus({
          kind: OCR_STATUS.ERROR,
          message: "Failed to load OCR text",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getSignedUrl, fileId, enabled]);

  return status;
}
